/**
 * TopBar — Sticky full-width header.
 *
 * Layout (LTR):
 *   [Logo + AppName]
 *   [ContextGridSwitcher]    (persona/role grid dropdown)
 *   [MegaMenuGrid]            (module launcher grid dropdown)
 *   (flex spacer)
 *   [AI credits chip]
 *   [Plan chip]
 *   [Language] [Theme] [Messages] [Billing] [Notifications]
 *   [Mode toggle]
 *   [User menu]
 *
 * All right-side actions are icon-only on small screens; labels show on md+.
 */

import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Bell, Moon, Sun, Globe, User, LogOut, Settings, Plus,
  MessageSquare, CreditCard, BrainCircuit, Sparkles,
  ToggleLeft, ToggleRight, Menu, Command, Keyboard, Search, Rss,
} from 'lucide-react'
import { useAuth } from '@/lib/auth/useAuth'
import { useTheme } from '@/components/providers/ThemeProvider'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn, getInitials } from '@/lib/utils'
import { useContextStore } from '@/stores/context-store'
import { useCreditBalance } from '@/services/credits.service'
import { getContextMeta, type GrcContextId } from '@/config/contexts'
import { tn } from '@/i18n/navLabels'
import { useModuleSwitcher } from '@/stores/module-switcher.store'
import { ContextGridSwitcher } from './ContextGridSwitcher'
import { ThemeSwitcher } from './ThemeSwitcher'
import { ClientSelector } from '@/components/ClientSelector'
import { ContextDescription } from './ContextDescription'
import { CurrencySelector } from './CurrencySelector'
import { GlobalSearch } from '@/components/copilot/GlobalSearch'
import { useCommandPalette } from '@/components/ui/command-palette'
import { openShortcutsHelp } from '@/components/ui/shortcuts-help'
import { useCollabUnread } from '@/hooks/useCollabUnread'
import { useFeedUnread } from '@/services/social-feed.service'
import { formatNumber } from '@/lib/jurisdiction/jurisdiction-config'

const CONTEXT_CREATE_ACTIONS: Partial<Record<GrcContextId, { label: string; href: string }[]>> = {
  associe: [
    { label: 'Nouveau Client', href: '/clients?create=1' },
    { label: 'Nouveau Mandat', href: '/mandats?create=1' },
    { label: 'Devis', href: '/offers' },
  ],
  'directeur-mission': [
    { label: 'Nouveau Mandat', href: '/mandats?create=1' },
    { label: 'Mission Audit', href: '/audit-pro/plan-wizard' },
    { label: 'Plan Qualité', href: '/audit-pro/actions' },
  ],
  'consultant-grc': [
    { label: 'Évaluation', href: '/maturity-pro/questionnaires' },
    { label: 'Risque', href: '/risk-pro/identify' },
    { label: 'Gap Analysis', href: '/compliance-pro/gap-analysis' },
  ],
  'expert-securite': [
    { label: 'Incident', href: '/security-pro/incidents' },
    { label: 'Vulnérabilité', href: '/security-pro/vulns' },
    { label: 'Contrôle', href: '/security-pro/controls' },
  ],
  'expert-donnees-ia': [
    { label: 'PIA', href: '/compliance-pro/pia' },
    { label: 'Modèle IA', href: '/ai-ia-pro/registre' },
    { label: 'Actif Données', href: '/data-pro/mdm' },
  ],
  auditeur: [
    { label: 'Mission Audit', href: '/audit-pro/plan-wizard' },
    { label: 'Constat', href: '/audit-pro/findings' },
    { label: 'Action Corrective', href: '/audit-pro/actions' },
  ],
  analyste: [
    { label: 'Évaluation', href: '/maturity-pro/questionnaires' },
    { label: 'Campagne', href: '/maturity-pro/campaigns' },
    { label: 'Rapport', href: '/dashboard-pro/rapport-ca' },
  ],
  formateur: [
    { label: 'Plan Changement', href: '/change-pro/roadmap' },
    { label: 'Formation', href: '/change-pro/formation' },
    { label: 'Communication', href: '/change-pro/communication' },
  ],
  administration: [
    { label: 'Inviter Membre', href: '/team' },
    { label: 'Organisation', href: '/admin/organizations' },
  ],
}

const COMMON_CREATE_ACTIONS = [
  { label: 'Nouveau Client', href: '/clients?create=1' },
  { label: 'Nouveau Mandat', href: '/mandats?create=1' },
]

export function TopBar({ onMenuClick }: { onMenuClick?: () => void } = {}) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { activeContext } = useContextStore()
  const { setActiveModule } = useModuleSwitcher()
  const { setOpen: setPaletteOpen } = useCommandPalette()
  const isMac =
    typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
  const { resolvedTheme, setTheme } = useTheme()
  const chatUnread = useCollabUnread()
  const { data: feedUnread } = useFeedUnread()
  // Real i18next-backed language toggle (was a dead label-only toggle). Reading
  // i18n.language keeps this reactive — useTranslation re-renders on languageChanged.
  const { t, i18n } = useTranslation()
  const language: 'fr' | 'en' = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'fr'
  const toggleLanguage = () => i18n.changeLanguage(language === 'fr' ? 'en' : 'fr')
  const toggleTheme = () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')

  const createActions = [
    ...(CONTEXT_CREATE_ACTIONS[activeContext] ?? []),
    ...COMMON_CREATE_ACTIONS,
  ]

  // Real-time AI-credit balance (plan + extra), refetched every 15s + on focus.
  const { data: creditBalance } = useCreditBalance()
  const aiCredits = {
    remaining: creditBalance?.ai?.total ?? 0,
    total:
      (creditBalance?.ai?.monthly_quota ?? 0) + (creditBalance?.ai?.purchased ?? 0) ||
      (creditBalance?.ai?.total ?? 0),
  }
  const planName = 'Pro'
  const isAgentMode = false

  useEffect(() => {
    const segment = location.pathname.split('/').filter(Boolean)[0] ?? 'dashboard'
    if (segment && segment.endsWith('-pro')) {
      setActiveModule(segment as never)
    }
  }, [location.pathname])

  return (
    <header className="sticky top-0 z-40 shrink-0 border-b border-[hsl(var(--topbar-border))] bg-[hsl(var(--topbar-bg))] text-[hsl(var(--topbar-fg))]">
      <div className="flex h-14 items-center gap-2 px-4">
        {/* Mobile/tablet hamburger — opens the sidebar drawer below lg (1024px)
            (#ADMINCLIENT-mobile-hamburger). */}
        {onMenuClick && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onMenuClick}
            className="lg:hidden h-8 w-8 shrink-0 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-white/10"
            aria-label="Ouvrir le menu"
            data-testid="mobile-nav-trigger"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}

        {/* Logo + AppName */}
        <Link to="/dashboard" className="flex items-center gap-2 shrink-0 mr-1">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-blue-400 shadow-md shadow-primary/20">
            <span className="text-white font-black text-[12px]">AC</span>
          </div>
          <div className="hidden md:flex flex-col leading-tight">
            <span className="text-[13px] font-extrabold text-[hsl(var(--topbar-fg))] tracking-tight">AdminClient</span>
            <span className="text-[9px] font-semibold text-[hsl(var(--topbar-fg))]/60 uppercase tracking-[0.18em]">SGRC</span>
          </div>
        </Link>

        <div className="h-6 w-px bg-[hsl(var(--topbar-border))] mx-1 shrink-0" />

        {/* Context grid switcher */}
        <ContextGridSwitcher align="start" />

        {/* Client selector — moved here from the sidebar so the left nav stays
            focused on navigation only. */}
        <div className="hidden lg:block">
          <ClientSelector collapsed={false} />
        </div>

        {/* Context description + feature shortcuts */}
        <ContextDescription />

        {/* Global semantic (RAG) search — knowledge-service */}
        <GlobalSearch className="hidden lg:block w-full max-w-[18rem] mx-2 shrink" />

        {/* Command palette launcher — discoverable ⌘K affordance */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                aria-label="Palette de commandes"
                className={cn(
                  'hidden md:flex items-center gap-2 h-8 rounded-full pl-2.5 pr-1.5 shrink-0',
                  'border border-[hsl(var(--topbar-fg))]/20 bg-[hsl(var(--topbar-fg))]/10 hover:bg-[hsl(var(--topbar-fg))]/20 transition-colors',
                  'text-[11px] font-medium text-[hsl(var(--topbar-fg))]/80',
                )}
              >
                <Search className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">{t('shell.commands')}</span>
                <kbd className="flex items-center gap-0.5 rounded border border-[hsl(var(--topbar-fg))]/25 bg-[hsl(var(--topbar-fg))]/10 px-1 py-0.5 font-mono text-[10px] leading-none">
                  {isMac ? <Command className="h-2.5 w-2.5" /> : 'Ctrl'}K
                </kbd>
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Palette de commandes — rechercher, naviguer, créer
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Mobile: compact palette launcher */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setPaletteOpen(true)}
          aria-label="Palette de commandes"
          className="md:hidden h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15"
        >
          <Search className="h-4 w-4" />
        </Button>

        {/* Keyboard-shortcuts help (?) */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => openShortcutsHelp()}
                aria-label="Raccourcis clavier"
                className="hidden md:inline-flex h-8 w-8 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-[hsl(var(--topbar-fg))]/15"
              >
                <Keyboard className="h-3.5 w-3.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Raccourcis clavier — appuyez sur <kbd className="font-mono">?</kbd>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Quick-create */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              className="h-8 rounded-full text-[12px] gap-1 shrink-0 shadow-sm shadow-primary/20"
            >
              <Plus className="h-3.5 w-3.5" />
              <span className="hidden lg:inline">{t('topbar.new')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="text-xs">{t('shell.quickActions')} — {getContextMeta(activeContext).name}</DropdownMenuLabel>
            {createActions.map((action) => (
              <DropdownMenuItem key={action.href} onClick={() => navigate(action.href)}>
                {tn(action.label)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* AI credits chip */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/account/credits"
                className={cn(
                  'hidden md:flex items-center gap-1.5 h-8 rounded-full px-2.5 shrink-0',
                  'border bg-accent/30 hover:bg-accent transition-colors',
                  'text-[11px] font-semibold',
                )}
                aria-label="Crédits IA"
              >
                <BrainCircuit className="h-3.5 w-3.5 text-primary" />
                <span className="tabular-nums text-[hsl(var(--topbar-fg))]">
                  {formatNumber(aiCredits.remaining)}
                </span>
                <span className="text-[hsl(var(--topbar-fg))]/85">/{formatNumber(aiCredits.total)}</span>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('shell.aiCreditsAvailable')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Plan chip */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                to="/account/subscription"
                className={cn(
                  'hidden md:flex items-center gap-1.5 h-8 rounded-full px-2.5 shrink-0',
                  'border bg-gradient-to-r from-primary/10 to-blue-400/10 hover:from-primary/15 hover:to-blue-400/15 transition-colors',
                  'text-[11px] font-semibold text-primary',
                )}
                aria-label="Plan"
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span>{planName}</span>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('shell.subscriptionPlan')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="h-5 w-px bg-[hsl(var(--topbar-border))] shrink-0 hidden md:block" />

        {/* Language */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleLanguage}
                className="hidden sm:flex h-8 gap-1 px-2 shrink-0 rounded-full text-[11px] font-semibold"
                aria-label="Langue"
              >
                <Globe className="h-3.5 w-3.5" />
                <span className="hidden lg:inline uppercase">{language}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Langue ({language.toUpperCase()})</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Currency */}
        <CurrencySelector />

        {/* Quick light/dark toggle */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-8 w-8 shrink-0 rounded-full text-[hsl(var(--topbar-fg))] hover:bg-white/10 hover:text-[hsl(var(--topbar-fg))]"
                aria-label="Mode clair/sombre"
              >
                {resolvedTheme === 'dark'
                  ? <Sun className="h-3.5 w-3.5" />
                  : <Moon className="h-3.5 w-3.5" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {resolvedTheme === 'dark' ? 'Thème clair' : 'Thème sombre'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Color-theme swatch picker (10 themes) — hidden on phones */}
        <div className="hidden md:flex">
          <ThemeSwitcher align="end" />
        </div>

        {/* Social feed — global network feed (SOCIAL_LAYER_PLAN §A). Visible to all contexts. */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/feed" className="hidden sm:inline-flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 shrink-0 rounded-full"
                  aria-label="Fil d'actualité"
                >
                  <Rss className="h-3.5 w-3.5" />
                  {feedUnread?.hasNew && (
                    <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-card" />
                  )}
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Fil d'actualité</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Messages — hidden on phones (reachable via nav) */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/global/messages" className="hidden md:inline-flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative h-8 w-8 shrink-0 rounded-full"
                  aria-label="Messages"
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                  {chatUnread > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 h-4 min-w-4 rounded-full bg-red-500 px-1 text-[9px] font-bold text-white flex items-center justify-center ring-2 ring-card">
                      {chatUnread > 9 ? '9+' : chatUnread}
                    </span>
                  )}
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Messagerie</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Billing — hidden on phones (reachable via user menu → Facturation) */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/billing" className="hidden md:inline-flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 rounded-full"
                  aria-label="Facturation"
                >
                  <CreditCard className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="bottom">Facturation</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Notifications */}
        <Link to="/notifications">
          <Button
            variant="ghost"
            size="icon"
            className="relative h-8 w-8 shrink-0 rounded-full"
            aria-label="Notifications"
          >
            <Bell className="h-3.5 w-3.5" />
            <span className="absolute -right-0.5 -top-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-card">
              3
            </span>
          </Button>
        </Link>

        {/* Mode (User / Agent) toggle — hidden on phones */}
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  'hidden md:flex h-8 gap-1.5 text-[11px] font-medium border-2 rounded-full transition-all shrink-0',
                  isAgentMode
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-muted text-muted-foreground',
                )}
              >
                {isAgentMode
                  ? <ToggleRight className="h-3.5 w-3.5" />
                  : <ToggleLeft className="h-3.5 w-3.5" />}
                <span className="hidden lg:inline">
                  {isAgentMode ? t('topbar.modeAgent') : t('topbar.modeUser')}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {isAgentMode
                ? 'Mode Agent IA actif'
                : 'Passer en Mode Agent IA'}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <div className="h-5 w-px bg-[hsl(var(--topbar-border))] shrink-0 hidden md:block" />

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="h-8 gap-2 shrink-0 rounded-full pl-1 pr-2"
            >
              <Avatar className="h-7 w-7 ring-2 ring-primary/20">
                <AvatarFallback className="bg-gradient-to-br from-primary to-blue-400 text-white text-[11px] font-bold">
                  {user?.name ? getInitials(user.name) : 'U'}
                </AvatarFallback>
              </Avatar>
              <span className="hidden lg:inline text-[12px] font-semibold text-foreground max-w-[100px] truncate">
                {user?.name ?? 'Utilisateur'}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuLabel>
              <div className="flex flex-col">
                <span className="font-semibold">{user?.name}</span>
                <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
                <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider text-primary">
                  <Sparkles className="h-2.5 w-2.5" /> {planName}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
              <User className="mr-2 h-4 w-4" />{t('topbar.profile')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/account/credits')}>
              <BrainCircuit className="mr-2 h-4 w-4" />{t('topbar.aiCredits')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/billing')}>
              <CreditCard className="mr-2 h-4 w-4" />{t('topbar.billing')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings/notifications')}>
              <Settings className="mr-2 h-4 w-4" />{t('topbar.settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />{t('topbar.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
